<template>
  <div class="game-category-lobby">
    <div class="content-wrapper">
      <div class="header-wrapper">
        <div v-if="title" class="lobby-title">{{ title }}</div>
        <div v-if="desc" class="lobby-desc">
          <span v-html="desc" />
        </div>
      </div>
      <div class="game-list">
        <GameCard
          v-for="(game, index) in gameCards"
          :key="`${game.category}-${index}`"
          :show-ribbon="category === GAME_CATEGORIES.all"
          v-bind="game"
          @item-clicked="handleGameClick(game)"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import GameCard from '@/components/cards/GameCard.vue';
  import { allGames, gameCards, sortFastplayGames, sortFishTableGames, sortPvpGames } from '@/data/games';
  import { GameLauncher } from '@/mixins/game-launcher';

  const GAME_CATEGORIES = {
    all: 'all',
    fastplay: 'FP',
    pvp: 'PVP',
    ftg: 'FishTable',
  };
  const TITLE_MAP = {
    all: 'ALL GAMES',
    fastplay: 'FASTPLAY',
    ftg: 'FISH TABLE GAMES',
    pvp: 'PLAYER vs PLAYER',
  };
  const DESC_MAP = {
    all: "Experience MaxQuest's original and exclusive games, where players can connect, compete, and win real crypto prizes",
    fastplay: 'Rapid action designed for quick and engaging solo play',
    ftg: 'MaxQuest dynamic Fish style shooter games - <b>Sector X</b>, <b>DragonStone</b> and <b>Mission Amazon.</b>',
    pvp: 'Enjoy fast-paced, cutting-edge PvP games where one player wins the entire prize pot.',
  };

  export default {
    name: 'GameCategoryLobbyComponent',

    mixins: [GameLauncher],

    components: {
      GameCard,
    },

    props: {
      category: {
        type: String,
        required: true,
        validator: (value) => Object.keys(GAME_CATEGORIES).includes(value),
      },
    },

    data: () => ({
      GAME_CATEGORIES,
    }),

    computed: {
      allGames() {
        return allGames.filter((game) => !game.comingSoon);
      },

      title() {
        return TITLE_MAP[this.category] || '';
      },

      desc() {
        return DESC_MAP[this.category] || '';
      },

      /**
       * Returns a filtered array of game cards that belong to the selected category.
       *
       * @return {{title: String, image: String, category: String, gameType: String}[]} An array of game cards
       */
      gameCards() {
        if (this.category === GAME_CATEGORIES.all) {
          return this.allGames;
        }

        if (this.category === 'ftg') {
          const list = gameCards.filter((game) => game.category === GAME_CATEGORIES.ftg);
          return sortFishTableGames(list);
        }

        const games = this.allGames.filter((game) => game.category === GAME_CATEGORIES[this.category]);

        if (this.category === 'fastplay') {
          return sortFastplayGames(games);
        } else {
          return sortPvpGames(games);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/scss/mixins';

  .game-category-lobby {
    background: #1c0630;
    container: game-category-lobby / inline-size;

    @media screen and (min-width: 1281px) {
      width: 100%;
      max-width: calc(100vw - 389px - 64px);
      margin-inline: auto;

      .content-wrapper {
        padding-inline: 0;
      }
    }

    @media screen and (min-width: 1842px) {
      max-width: 1448px;
      padding-right: 32px;
    }
  }

  @container game-category-lobby (min-width: 0px) {
    .content-wrapper {
      max-width: 1448px;
      margin-inline: auto;
      padding-inline: $padding-small;
    }

    .header-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 24px;
      border-radius: 10px;
      background: #24073d;
    }

    .lobby-title {
      color: #fff;
      font-size: 30px;
      font-weight: 700;
      line-height: 150%;
      letter-spacing: -0.64px;
      padding-top: 20px;
      padding-bottom: 20px;
    }

    .lobby-desc {
      display: flex;
      padding: 24px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      align-self: stretch;

      span {
        color: #fff;
        text-align: center;
        font-size: 18px;
        font-weight: 400;
        line-height: 150%;

        &::v-deep b {
          font-weight: 500;
        }
      }
    }

    .game-list {
      display: grid;
      grid-template-columns: 1fr 1fr;
      justify-items: center;
      justify-content: center;
      margin-top: 24px;
      row-gap: 24px;
      column-gap: 16px;
      margin-bottom: 60px;

      .game-card {
        max-width: none;
      }
    }
  }

  @container game-category-lobby (min-width: 480px) {
    .content-wrapper {
      padding-inline: $padding-medium;
    }
  }

  @container game-category-lobby (min-width: 768px) {
    .content-wrapper {
      padding-inline: $padding-large;
    }
  }

  @container game-category-lobby (min-width: 808px) {
    .header-wrapper {
      flex-direction: row;
      justify-content: center;
      padding: 48px 60px;
      gap: 60px;
    }

    .lobby-title {
      display: flex;
      justify-content: center;
      flex-basis: 35%;
      flex-shrink: 0;
      padding: 0;
    }

    .lobby-desc {
      flex-basis: 65%;
      justify-content: flex-start;
      padding: 0;
      background: none;

      span {
        text-align: left;
      }
    }

    .game-list {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  @container game-category-lobby (min-width: 1024px) {
    .content-wrapper {
      padding-inline: $padding-xlarge;
    }
  }

  @container game-category-lobby (min-width: 1032px) {
    .lobby-title {
      font-size: 40px;
    }

    .lobby-desc span {
      font-size: 28px;
    }
  }

  @container game-category-lobby (min-width: 1280px) {
    .content-wrapper {
      padding-inline: $padding-xxlarge;
    }
  }

  @container game-category-lobby (min-width: 1448px) {
    .content-wrapper {
      @media screen and (min-width: 1680px) {
        padding-inline: 0;
      }
    }
  }
</style>
